import axios, {AxiosError, AxiosRequestConfig, AxiosResponse} from "axios";

export interface Result {
  readonly response?: AxiosResponse;
  readonly error?: AxiosError;
}

export class APIError extends Error {
  readonly statusCode: number;

  constructor(statusCode: number, message: string) {
    super(message);
    this.statusCode = statusCode;
  }
}

const defaultConfig: AxiosRequestConfig = {
  timeout: 10000
};

export const get = async <T>(path: string, c: AxiosRequestConfig): Promise<T> => {
  const config: AxiosRequestConfig = {
    ...defaultConfig,
    ...c
  };

  try {
    const response: AxiosResponse = await axios.get(path, config);
    return handleResult({response});
  } catch (error) {
    return handleResult({
      response: (error as AxiosError).response,
      error: error as AxiosError
    });
  }
};

export const post = async <T>(path: string, body: any, c: AxiosRequestConfig): Promise<T> => {
  const config: AxiosRequestConfig = {
    ...defaultConfig,
    ...c
  };

  try {
    const response: AxiosResponse = await axios.post(path, body, config);
    return handleResult({response});
  } catch (error) {
    return handleResult({
      error: error as AxiosError
    });
  }
};

export const del = async <T>(path: string, c: AxiosRequestConfig): Promise<T> => {
  const config: AxiosRequestConfig = {
    ...defaultConfig,
    ...c
  };

  try {
    const response: AxiosResponse = await axios.delete(path, config);
    return handleResult({response});
  } catch (error) {
    return handleResult({
      error: error as AxiosError
    });
  }
};

export const patch = async <T>(path: string, body: any, c: AxiosRequestConfig): Promise<T> => {
  const config: AxiosRequestConfig = {
    ...defaultConfig,
    ...c
  };

  try {
    const response: AxiosResponse = await axios.patch(path, body, config);
    return handleResult({response});
  } catch (error) {
    return handleResult({
      error: error as AxiosError
    });
  }
};

const handleResult = <T>(result: Result): T => {
  const response = result.response || result.error?.response;

  if (!response) {
    throw new APIError(500, "No response in api call result");
  }

  if (result.error) {
    if (result.error.response) {
      throw new APIError(result.error.response.status, JSON.stringify(result.error.response.data));
    }

    throw new APIError(parseInt(result.error.code || "500", 10), result.error.message);
  }

  const status: number = result.response?.status || 500;
  if (status >= 400) {
    throw new APIError(status, JSON.stringify(result.response?.data));
  }

  return response.data as T;
};
